import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import type { DayBlock, DayView, TripLeg } from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useLibraryStore } from "../../store/libraryStore";
import { useUIStore } from "../../store/uiStore";
import {
  useCalendarDndStore,
  type DateRangePreview,
} from "../../store/calendarDndStore";
import { computeDayViews } from "../../utils/dayViews";
import {
  addMinutes,
  addDaysToDate,
  daysBetweenDates,
  enumerateDates,
  eventDisplayTimeInfo,
  minutesBetween,
  zonedDate,
  zonedFromDateAndTime,
} from "../../utils/zonedTime";
import { dndIds } from "../../utils/dndIds";
import { useLegRangeGesture } from "./useLegRangeGesture";
import {
  legSegmentForDate,
  packLegLanes,
  type LegSegment,
} from "../../utils/legLanes";
import { legPalette, legPalettes } from "../../theme/colors";
import { useLegStore } from "../../store/legStore";
import {
  deriveAccommodationStaySegments,
  type AccommodationStaySegment,
} from "../../utils/accommodationStays";
import { accommodationPalettes } from "../../theme/colors";
import {
  buildAccommodationRangeChange,
  moveIntervalEdgeToDate,
  resizeDateRange,
  stayRangeFromSegment,
  type RangeEdge,
} from "../../utils/calendarRangeResize";
import {
  clampRollingWindowStart,
  rollingWindowDates,
  rollingWindowLimits,
} from "../../utils/rollingCalendar";
import { resolvePlannerCalendarStart } from "../../utils/plannerDays";
import { requiresCrossZoneGestureConfirmation } from "../../utils/calendarGestures";
import { Button } from "../Button";
import type { BoardDragData, CalendarDateDropData } from "./dragTypes";
import {
  DateRangeResizeHandle,
  type DateRangeResizeTarget,
} from "./DateRangeResizeHandle";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_CHIPS = 3;
const RANGE_SELECTION_INTENT_PX = 4;

function MonthBlockChip({
  block,
  date,
  displayZone,
  label,
  isReadOnly,
  onAutoNavigate,
}: {
  block: DayBlock;
  date: string;
  displayZone: string;
  label: string;
  isReadOnly: boolean;
  onAutoNavigate: (direction: -1 | 1) => void;
}) {
  const locked =
    block.type === "poi" ? block.scheduledPOI.locked : block.note.locked;
  const startTime =
    block.type === "poi" ? block.scheduledPOI.startTime : block.note.startTime;
  const endTime =
    block.type === "poi" ? block.scheduledPOI.endTime : block.note.endTime;
  const dragData: BoardDragData =
    block.type === "poi"
      ? {
          type: "scheduled-poi",
          scheduledPoiId: block.scheduledPOI.id,
          poiId: block.scheduledPOI.poiId,
          startTime,
          originDisplayDate: date,
          originDisplayZone: displayZone,
          spanMinutes: minutesBetween(startTime, endTime),
          label,
        }
      : {
          type: "note",
          noteId: block.note.id,
          startTime,
          originDisplayDate: date,
          originDisplayZone: displayZone,
          spanMinutes: minutesBetween(startTime, endTime),
          label,
        };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id:
      block.type === "poi"
        ? dndIds.monthPoiSegment(block.id, date)
        : dndIds.monthNoteSegment(block.id, date),
    data: dragData,
    disabled: isReadOnly || locked || !block.segment.isStart,
  });
  const wasDragging = useRef(false);
  useEffect(() => {
    if (isDragging) {
      wasDragging.current = true;
      return;
    }
    if (!wasDragging.current) return;
    const reset = window.setTimeout(() => {
      wasDragging.current = false;
    }, 0);
    return () => window.clearTimeout(reset);
  }, [isDragging]);
  const updateScheduledPoi = useTripStore((state) => state.updateScheduledPoi);
  const updateNote = useTripStore((state) => state.updateNote);
  const openScheduledVisitEditor = useUIStore(
    (state) => state.openScheduledVisitEditor,
  );
  const openNoteEditor = useUIStore((state) => state.openNoteEditor);
  const trip = useTripStore((state) => state.trip);
  const timezoneOverrides = useTripStore(
    (state) => state.schedule.timezoneOverrides,
  );
  const zoneForDate = (targetDate: string) =>
    timezoneOverrides[targetDate] ?? trip?.defaultTimezone ?? "UTC";
  const previewForEdge = (
    edge: RangeEdge,
    target: DateRangeResizeTarget,
  ): DateRangePreview => {
    const moved = moveIntervalEdgeToDate(
      startTime,
      endTime,
      edge,
      target.date,
      target.zone,
      displayZone,
    );
    return {
      startDate: moved ? zonedDate(moved.startTime) : target.date,
      endDate: moved ? zonedDate(moved.endTime) : target.date,
      targetDate: target.date,
      label: `${label} ${edge}`,
      invalid: !moved,
      conflict: false,
    };
  };
  const commitEdge = (edge: RangeEdge, target: DateRangeResizeTarget) => {
    const moved = moveIntervalEdgeToDate(
      startTime,
      endTime,
      edge,
      target.date,
      target.zone,
      displayZone,
    );
    if (!moved) return;
    if (requiresCrossZoneGestureConfirmation(displayZone, target.zone)) {
      if (block.type === "poi") {
        openScheduledVisitEditor(block.scheduledPOI.id, {
          eventType: "scheduledVisit",
          entityId: block.scheduledPOI.id,
          gesture: "resize",
          ...moved,
          originalDisplayZone: displayZone,
          targetDisplayZone: target.zone,
        });
      } else {
        openNoteEditor("edit", block.note.id, undefined, {
          eventType: "note",
          entityId: block.note.id,
          gesture: "resize",
          ...moved,
          originalDisplayZone: displayZone,
          targetDisplayZone: target.zone,
        });
      }
      return;
    }
    if (block.type === "poi") updateScheduledPoi(block.scheduledPOI.id, moved);
    else updateNote(block.note.id, moved);
  };

  const timeInfo = eventDisplayTimeInfo(startTime, endTime, displayZone, {
    clippedTop: !block.segment.isStart,
    clippedBottom: !block.segment.isEnd,
  });
  const content = (
    <>
      {timeInfo.nativeRange && (
        <span
          className="material-symbols-outlined shrink-0 text-2xs leading-none"
          aria-label={`Native time: ${timeInfo.nativeRange}`}
        >
          public
        </span>
      )}
      <span className="truncate">{label}</span>
    </>
  );
  const canDrag = block.segment.isStart && !locked && !isReadOnly;

  const classes = clsx(
    "flex w-full items-center gap-1 overflow-hidden rounded px-1.5 py-px text-left text-2xs font-bold focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-info",
    block.type === "poi"
      ? "border border-border bg-page text-primary"
      : "bg-slate-800 text-slate-200",
    canDrag ? "cursor-grab touch-none" : "cursor-pointer",
    isDragging && "opacity-35",
  );
  const openInstanceEditor = () => {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    if (block.type === "poi") openScheduledVisitEditor(block.scheduledPOI.id);
    else openNoteEditor("edit", block.note.id);
  };
  const editorKind = block.type === "poi" ? "scheduled visit" : "note";
  const chip = (
    <Button
      ref={setNodeRef}
      variant="unstyled"
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
      onClick={(event) => {
        event.stopPropagation();
        openInstanceEditor();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        openInstanceEditor();
      }}
      className={classes}
      title={
        canDrag
          ? `Drag to move ${label}; click to open its ${editorKind}. ${timeInfo.tooltip}`
          : `Open ${label} ${editorKind}. ${timeInfo.tooltip}`
      }
      aria-label={`Open ${label} for ${date}`}
      data-testid={`month-${block.type}-${block.id}-${date}`}
    >
      {content}
    </Button>
  );

  return (
    <div className="group/range relative">
      {chip}
      {!locked && !isReadOnly && block.segment.isStart && (
        <DateRangeResizeHandle
          edge="start"
          label={label}
          currentDate={date}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("start", target)}
          commitTarget={(target) => commitEdge("start", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
      {!locked && !isReadOnly && block.segment.isEnd && (
        <DateRangeResizeHandle
          edge="end"
          label={label}
          currentDate={date}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("end", target)}
          commitTarget={(target) => commitEdge("end", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
    </div>
  );
}

function MonthDateCell({
  date,
  zone,
  children,
}: {
  date: string;
  zone: string;
  children: React.ReactNode;
}) {
  const preview = useCalendarDndStore((state) => state.dateRangePreview);
  const inPreview = Boolean(
    preview && date >= preview.startDate && date <= preview.endDate,
  );
  const isPreviewTarget = preview?.targetDate === date;
  const { setNodeRef, isOver } = useDroppable({
    id: dndIds.calendarDate(date),
    data: { type: "calendar-date", date, zone } satisfies CalendarDateDropData,
  });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "flex min-h-24 min-w-0 flex-col gap-1 rounded-lg border bg-surface px-2 py-1.5 text-left transition-shadow",
        isOver
          ? "border-info bg-sky-50 shadow-[0_0_0_1px_var(--color-info)]"
          : inPreview
            ? preview?.invalid || preview?.conflict
              ? "border-error bg-error-bg shadow-[0_0_0_1px_var(--color-error)]"
              : "border-info bg-sky-50 shadow-[0_0_0_1px_var(--color-info)]"
            : "border-border hover:shadow-md",
        isPreviewTarget && "ring-2 ring-inset ring-current",
      )}
      data-calendar-month-date={date}
      data-calendar-date-target={date}
      data-calendar-zone={zone}
    >
      {children}
    </div>
  );
}

/**
 * A hover-revealed create affordance in the bottom of a month cell. Clicking
 * acts on this one date; dragging across cells sweeps out an inclusive range
 * and previews it through the shared date-range preview the drag layer already
 * renders. Notes and legs both create from a date range, so they share the
 * gesture and differ only in what they do with the result.
 */
function MonthQuickAddAction({
  date,
  label,
  ariaLabel,
  title,
  previewLabel,
  onSelect,
}: {
  date: string;
  label: string;
  ariaLabel: string;
  title: string;
  /** Shown on the drag preview while the range is being swept out. */
  previewLabel: string;
  /** `dragged` distinguishes a swept range from a plain click on one date. */
  onSelect: (startDate: string, endDate: string, dragged: boolean) => void;
}) {
  const setDateRangePreview = useCalendarDndStore(
    (state) => state.setDateRangePreview,
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const suppressClick = useRef(false);

  const targetDateAtPoint = (clientX: number, clientY: number) =>
    document
      .elementsFromPoint(clientX, clientY)
      .map((element) =>
        element.closest<HTMLElement>("[data-calendar-month-date]"),
      )
      .find((element): element is HTMLElement =>
        Boolean(element?.dataset.calendarMonthDate),
      )?.dataset.calendarMonthDate ?? null;

  const orderedRange = (targetDate: string) =>
    date <= targetDate
      ? { startDate: date, endDate: targetDate }
      : { startDate: targetDate, endDate: date };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    const handle = event.currentTarget;
    const pointerId = event.pointerId;
    const originClientX = event.clientX;
    const originClientY = event.clientY;
    let selecting = false;
    let targetDate: string | null = null;

    handle.setPointerCapture(pointerId);

    const cleanup = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleCancel);
      if (handle.hasPointerCapture(pointerId))
        handle.releasePointerCapture(pointerId);
    };
    const handleMove = (moveEvent: PointerEvent) => {
      if (
        !selecting &&
        Math.hypot(
          moveEvent.clientX - originClientX,
          moveEvent.clientY - originClientY,
        ) <= RANGE_SELECTION_INTENT_PX
      ) {
        return;
      }
      if (!selecting) setIsSelecting(true);
      selecting = true;
      moveEvent.preventDefault();
      targetDate = targetDateAtPoint(moveEvent.clientX, moveEvent.clientY);
      if (!targetDate) {
        setDateRangePreview(null);
        return;
      }
      const range = orderedRange(targetDate);
      setDateRangePreview({
        ...range,
        targetDate,
        label: previewLabel,
        invalid: false,
        conflict: false,
      });
    };
    const handleUp = () => {
      cleanup();
      setIsSelecting(false);
      setDateRangePreview(null);
      if (!selecting || !targetDate) return;
      suppressClick.current = true;
      window.setTimeout(() => {
        suppressClick.current = false;
      }, 0);
      const range = orderedRange(targetDate);
      onSelect(range.startDate, range.endDate, true);
    };
    const handleCancel = () => {
      cleanup();
      setIsSelecting(false);
      suppressClick.current = false;
      setDateRangePreview(null);
    };

    window.addEventListener("pointermove", handleMove, { passive: false });
    window.addEventListener("pointerup", handleUp, { once: true });
    window.addEventListener("pointercancel", handleCancel, { once: true });
  };

  return (
    <Button
      variant="unstyled"
      className="calendar-month-quick-add flex min-h-6 w-full min-w-0 flex-1 basis-0 touch-none items-center justify-center gap-0.5 rounded-md border border-dashed border-border bg-surface/80 px-1 py-0.5 text-2xs font-bold text-tertiary shadow-sm transition-[opacity,transform,color,background-color,border-color] hover:border-info/50 hover:bg-sky-50 hover:text-info focus-visible:border-info focus-visible:bg-sky-50 focus-visible:text-info focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-info"
      onPointerDown={handlePointerDown}
      onTouchStart={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        if (suppressClick.current) {
          suppressClick.current = false;
          event.preventDefault();
          return;
        }
        onSelect(date, date, false);
      }}
      aria-label={ariaLabel}
      title={title}
      data-range-selecting={isSelecting || undefined}
    >
      {/* A text plus rather than the Material Symbols "add": the icon font
          reserves a 24px advance at any font size, which is most of the width
          a month cell can give each of two side-by-side actions. */}
      <span className="shrink-0 leading-none" aria-hidden="true">
        +
      </span>
      <span className="truncate">{label}</span>
    </Button>
  );
}

/**
 * One date's slice of a leg. The month grid is a cell-per-day grid rather than
 * a spanning one, so a leg that crosses a week boundary renders as several
 * segments — rounded only where it genuinely starts or ends, marked with a
 * continuation chevron where it runs on, and labelled at the start of each week
 * row so a continuing leg stays identifiable.
 *
 * The gestures mirror the all-day note chip beside it: drag the start segment
 * to move the whole range, drag an edge handle to stretch it. Both persist
 * through the granular PATCH rather than the full-trip autosave.
 */
function MonthLegChip({
  leg,
  segment,
  date,
  showLabel,
  isReadOnly,
  zoneForDate,
  onAutoNavigate,
}: {
  leg: TripLeg;
  segment: LegSegment;
  date: string;
  showLabel: boolean;
  isReadOnly: boolean;
  zoneForDate: (date: string) => string;
  onAutoNavigate: (direction: -1 | 1) => void;
}) {
  const openLegEditor = useUIStore((state) => state.openLegEditor);
  const palette = legPalette(leg.color);
  const canChange = !isReadOnly;

  const dragData: BoardDragData = {
    type: "leg-range",
    legId: leg.id,
    startDate: leg.startDate,
    endDate: leg.endDate,
    grabbedIndex: segment.index,
    dateCount: segment.count,
    label: leg.name,
  };
  // Only the start segment drags: a leg spanning several week rows would
  // otherwise offer the same move gesture from every cell it touches.
  const canDrag = canChange && segment.isStart;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.monthLegSegment(leg.id, date),
    data: dragData,
    disabled: !canDrag,
  });

  const { wasDragging, previewForEdge, commitEdge } = useLegRangeGesture(
    leg,
    isDragging,
  );

  const chip = (
    <Button
      ref={setNodeRef}
      variant="unstyled"
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
      disabled={isReadOnly}
      onClick={(event) => {
        event.stopPropagation();
        if (wasDragging.current) {
          wasDragging.current = false;
          return;
        }
        openLegEditor(leg.id);
      }}
      className={clsx(
        "flex h-4 w-full min-w-0 items-center gap-1 border px-1.5 text-2xs font-extrabold focus-visible:z-20 focus-visible:outline-2 focus-visible:outline-offset-1",
        segment.isStart ? "rounded-l-full" : "rounded-l-none border-l-0",
        segment.isEnd ? "rounded-r-full" : "rounded-r-none border-r-0",
        canDrag
          ? "cursor-grab touch-none hover:brightness-95 active:cursor-grabbing"
          : !isReadOnly && "cursor-pointer hover:brightness-95",
        isDragging && "opacity-30",
      )}
      style={{
        backgroundColor: palette.bg,
        borderColor: palette.border,
        color: palette.text,
      }}
      title={
        canDrag
          ? `${leg.name} · ${leg.startDate} – ${leg.endDate}. Drag to move, drag an edge to change dates, or click to edit.`
          : `${leg.name} · ${leg.startDate} – ${leg.endDate}`
      }
      aria-label={`Open leg ${leg.name}`}
      data-testid={dndIds.monthLegSegment(leg.id, date)}
      data-leg-segment-start={segment.isStart ? "true" : "false"}
      data-leg-segment-end={segment.isEnd ? "true" : "false"}
      data-leg-segment-clipped-top={segment.clippedTop ? "true" : "false"}
      data-leg-segment-clipped-bottom={segment.clippedBottom ? "true" : "false"}
    >
      {!segment.isStart && <span aria-hidden>◀</span>}
      {showLabel && <span className="min-w-0 flex-1 truncate">{leg.name}</span>}
      {!segment.isEnd && (
        <span className="ml-auto" aria-hidden>
          ▶
        </span>
      )}
    </Button>
  );

  return (
    // `group/range` is the name DateRangeResizeHandle reveals itself under.
    <div className="group/range relative min-w-0">
      {chip}
      {canChange && segment.isStart && (
        <DateRangeResizeHandle
          edge="start"
          label={leg.name}
          currentDate={leg.startDate}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("start", target)}
          commitTarget={(target) => commitEdge("start", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
      {canChange && segment.isEnd && (
        <DateRangeResizeHandle
          edge="end"
          label={leg.name}
          currentDate={leg.endDate}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("end", target)}
          commitTarget={(target) => commitEdge("end", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
    </div>
  );
}

function MonthAllDayChip({
  block,
  date,
  isReadOnly,
  zoneForDate,
  onAutoNavigate,
}: {
  block: DayView["allDayBlocks"][number];
  date: string;
  isReadOnly: boolean;
  zoneForDate: (date: string) => string;
  onAutoNavigate: (direction: -1 | 1) => void;
}) {
  const openNoteEditor = useUIStore((state) => state.openNoteEditor);
  const updateNote = useTripStore((state) => state.updateNote);
  const trip = useTripStore((state) => state.trip);
  const note = block.note;
  const canChange = !isReadOnly && !note.locked;
  const dragData: BoardDragData = {
    type: "all-day-note",
    noteId: note.id,
    startDate: note.allDayStartDate,
    endDate: note.allDayEndDate,
    grabbedIndex: block.segment.index,
    dateCount: block.segment.count,
    label: note.name,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.monthAllDayNoteSegment(note.id, date),
    data: dragData,
    disabled: !canChange,
  });
  const wasDragging = useRef(false);
  useEffect(() => {
    if (isDragging) {
      wasDragging.current = true;
      return;
    }
    if (!wasDragging.current) return;
    const reset = window.setTimeout(() => {
      wasDragging.current = false;
    }, 0);
    return () => window.clearTimeout(reset);
  }, [isDragging]);

  const previewForEdge = (
    edge: RangeEdge,
    target: DateRangeResizeTarget,
  ): DateRangePreview => {
    const resized = trip
      ? resizeDateRange(
          note.allDayStartDate,
          note.allDayEndDate,
          edge,
          target.date,
          trip.startDate,
          trip.endDate,
        )
      : null;
    return {
      startDate: resized?.startDate ?? target.date,
      endDate: resized?.endDate ?? target.date,
      targetDate: target.date,
      label: `${note.name} ${edge}`,
      invalid: !resized,
      conflict: false,
    };
  };
  const commitEdge = (edge: RangeEdge, target: DateRangeResizeTarget) => {
    if (!trip) return;
    const resized = resizeDateRange(
      note.allDayStartDate,
      note.allDayEndDate,
      edge,
      target.date,
      trip.startDate,
      trip.endDate,
    );
    if (
      !resized ||
      (resized.startDate === note.allDayStartDate &&
        resized.endDate === note.allDayEndDate)
    ) {
      return;
    }
    updateNote(note.id, {
      allDayStartDate: resized.startDate,
      allDayEndDate: resized.endDate,
    });
  };

  const chip = (
    <Button
      ref={setNodeRef}
      variant="unstyled"
      {...(canChange ? attributes : {})}
      {...(canChange ? listeners : {})}
      disabled={isReadOnly}
      onClick={(event) => {
        event.stopPropagation();
        if (wasDragging.current) {
          wasDragging.current = false;
          return;
        }
        openNoteEditor("edit", note.id);
      }}
      className={clsx(
        "flex w-full min-w-0 items-center gap-1 bg-calendar-all-day-bg-strong px-1.5 py-px text-left text-2xs font-extrabold text-calendar-all-day-text focus-visible:z-20 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-calendar-all-day-border",
        block.segment.isStart ? "rounded-l" : "rounded-l-sm",
        block.segment.isEnd ? "rounded-r" : "rounded-r-sm",
        canChange
          ? "cursor-grab touch-none hover:brightness-95 active:cursor-grabbing"
          : "cursor-pointer",
        isDragging && "opacity-30",
      )}
      title={
        canChange
          ? `Drag to move ${note.name}; click to edit`
          : `${note.name} · all day`
      }
      aria-label={`Open ${note.name} for ${date}`}
      data-testid={`month-all-day-note-${note.id}-${date}`}
    >
      <span className="material-symbols-outlined text-xs" aria-hidden>
        calendar_today
      </span>
      {!block.segment.isStart && <span aria-hidden>◀</span>}
      <span className="min-w-0 flex-1 truncate">{note.name}</span>
      {note.locked && (
        <span className="material-symbols-outlined text-xs" aria-label="Locked">
          lock
        </span>
      )}
      {!block.segment.isEnd && <span aria-hidden>▶</span>}
    </Button>
  );

  return (
    <div className="group/range relative min-w-0">
      {chip}
      {canChange && block.segment.isStart && (
        <DateRangeResizeHandle
          edge="start"
          label={note.name}
          currentDate={note.allDayStartDate}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("start", target)}
          commitTarget={(target) => commitEdge("start", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
      {canChange && block.segment.isEnd && (
        <DateRangeResizeHandle
          edge="end"
          label={note.name}
          currentDate={note.allDayEndDate}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("end", target)}
          commitTarget={(target) => commitEdge("end", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
    </div>
  );
}

function MonthAccommodation({
  date,
  segment,
  name,
  colorIndex,
  columnIndex,
  isReadOnly,
  onOpen,
  zoneForDate,
  onAutoNavigate,
}: {
  date: string;
  segment: AccommodationStaySegment | undefined;
  name: string | null;
  colorIndex: number;
  columnIndex: number;
  isReadOnly: boolean;
  onOpen: () => void;
  zoneForDate: (date: string) => string;
  onAutoNavigate: (direction: -1 | 1) => void;
}) {
  const sleep = useTripStore((state) => state.schedule.sleep);
  const setSleepAssignments = useTripStore(
    (state) => state.setSleepAssignments,
  );
  const stayRange = segment
    ? stayRangeFromSegment(segment.date, segment.index, segment.count)
    : null;
  const dragData: BoardDragData | undefined =
    segment && stayRange
      ? {
          type: "accommodation",
          poiId: segment.poiId,
          label: name ?? "Unknown accommodation",
          startDate: stayRange.start,
          endDate: stayRange.end,
          grabbedIndex: segment.index,
          nightCount: segment.count,
          colorIndex,
        }
      : undefined;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: segment
      ? dndIds.monthAccommodationSegment(segment.poiId, date)
      : `month-accommodation-unassigned-${date}`,
    data: dragData,
    disabled: isReadOnly || !segment,
  });
  const wasDragging = useRef(false);
  useEffect(() => {
    if (isDragging) {
      wasDragging.current = true;
      return;
    }
    if (!wasDragging.current) return;
    const reset = window.setTimeout(() => {
      wasDragging.current = false;
    }, 0);
    return () => window.clearTimeout(reset);
  }, [isDragging]);
  const nightLabel = new Date(`${date}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  if (!segment) {
    const unassignedContent = (
      <>
        <span
          className="material-symbols-outlined shrink-0 text-xs"
          aria-hidden
        >
          hotel
        </span>
        <span className="min-w-0 truncate">Unassigned</span>
      </>
    );
    return isReadOnly ? (
      <div className="mt-auto flex items-center gap-1 px-1 py-px text-2xs font-semibold text-tertiary/70">
        {unassignedContent}
      </div>
    ) : (
      <Button
        variant="unstyled"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        className="mt-auto flex min-w-0 w-full items-center gap-1 overflow-hidden rounded-md px-1 py-px text-left text-2xs font-semibold text-tertiary/70 transition-colors hover:bg-subtle hover:text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
        aria-label={`Choose accommodation for ${nightLabel}. Currently unassigned`}
        aria-haspopup="dialog"
        title={`Choose accommodation for ${nightLabel}`}
        data-testid={`month-accommodation-${date}`}
      >
        {unassignedContent}
      </Button>
    );
  }
  const palette =
    accommodationPalettes[colorIndex] ?? accommodationPalettes[0]!;
  if (!stayRange) return null;
  const previewForEdge = (
    edge: RangeEdge,
    target: DateRangeResizeTarget,
  ): DateRangePreview => {
    const start = edge === "start" ? target.date : stayRange.start;
    const end = edge === "end" ? target.date : stayRange.end;
    const change = buildAccommodationRangeChange(
      sleep,
      segment.poiId,
      stayRange.start,
      stayRange.end,
      start,
      end,
    );
    return {
      startDate: change ? start : target.date,
      endDate: change ? end : target.date,
      targetDate: target.date,
      label: `${name ?? "Accommodation"} ${edge}`,
      invalid: !change,
      conflict: Boolean(change?.conflicts.length),
    };
  };
  const commitEdge = (edge: RangeEdge, target: DateRangeResizeTarget) => {
    const start = edge === "start" ? target.date : stayRange.start;
    const end = edge === "end" ? target.date : stayRange.end;
    const change = buildAccommodationRangeChange(
      sleep,
      segment.poiId,
      stayRange.start,
      stayRange.end,
      start,
      end,
    );
    if (!change) return;
    if (
      change.conflicts.length > 0 &&
      !window.confirm(
        `${change.conflicts.length} night${change.conflicts.length === 1 ? "" : "s"} already have another accommodation. Replace ${change.conflicts.join(", ")}?`,
      )
    )
      return;
    setSleepAssignments(change.updates);
  };
  const startsRow = columnIndex === 0;
  const endsRow = columnIndex === 6;
  const continuesFromPreviousRow = startsRow && !segment.isStart;
  const continuesIntoNextRow = endsRow && !segment.isEnd;
  const content = (
    <>
      {continuesFromPreviousRow && (
        <span
          className="material-symbols-outlined -ml-1 shrink-0 text-2xs leading-none opacity-60"
          aria-hidden="true"
        >
          chevron_left
        </span>
      )}
      <span className="material-symbols-outlined shrink-0 text-sm">hotel</span>
      <span className="truncate" title={name ?? "Unknown accommodation"}>
        {name ?? "Unknown accommodation"}
      </span>
      {segment.count > 1 && segment.isStart && (
        <span className="ml-auto shrink-0 text-2xs opacity-70">
          {segment.count}n
        </span>
      )}
      {continuesIntoNextRow && (
        <span
          className="material-symbols-outlined ml-auto shrink-0 text-2xs leading-none opacity-60"
          aria-hidden="true"
        >
          chevron_right
        </span>
      )}
    </>
  );
  const className = clsx(
    "mt-auto flex h-6 min-w-0 w-full max-w-full items-center gap-1 overflow-hidden border-y px-1.5 text-2xs font-bold",
    segment.isStart
      ? "rounded-l-md border-l"
      : startsRow && "rounded-l-sm border-l",
    segment.isEnd
      ? "rounded-r-md border-r"
      : endsRow && "rounded-r-sm border-r",
    !isReadOnly &&
      "cursor-grab touch-none hover:brightness-95 active:cursor-grabbing",
    isDragging && "opacity-30",
  );
  const style = {
    background: palette.header,
    borderColor: palette.border,
    color: palette.text,
  };
  const accommodation = isReadOnly ? (
    <div className={className} style={style}>
      {content}
    </div>
  ) : (
    <Button
      ref={setNodeRef}
      variant="unstyled"
      {...attributes}
      {...listeners}
      onClick={(event) => {
        event.stopPropagation();
        if (wasDragging.current) {
          wasDragging.current = false;
          return;
        }
        onOpen();
      }}
      className={clsx(
        className,
        "focus-visible:z-20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info",
      )}
      style={style}
      aria-label={`Change accommodation for ${nightLabel}. Currently ${name ?? "unknown accommodation"}`}
      aria-haspopup="dialog"
      title={`Drag to move this stay; click to change accommodation for ${nightLabel}`}
      data-testid={`month-accommodation-${segment.date}`}
      data-column-index={columnIndex}
      data-stay-start={segment.isStart}
      data-stay-end={segment.isEnd}
      data-row-start-continuation={continuesFromPreviousRow}
      data-row-end-continuation={continuesIntoNextRow}
    >
      {content}
    </Button>
  );

  if (isReadOnly) return accommodation;
  return (
    <div className="group/range relative mt-auto min-w-0 w-full max-w-full">
      {accommodation}
      {segment.isStart && (
        <DateRangeResizeHandle
          edge="start"
          label={name ?? "accommodation"}
          currentDate={stayRange.start}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("start", target)}
          commitTarget={(target) => commitEdge("start", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
      {segment.isEnd && (
        <DateRangeResizeHandle
          edge="end"
          label={name ?? "accommodation"}
          currentDate={stayRange.end}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("end", target)}
          commitTarget={(target) => commitEdge("end", target)}
          onAutoNavigate={onAutoNavigate}
          autoNavigateRootSelector='[data-testid="calendar-month-view"]'
        />
      )}
    </div>
  );
}

function formatRange(start: string, end: string): string {
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();
  const startLabel = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
  const endLabel = endDate.toLocaleDateString("en-US", {
    month: sameMonth ? undefined : "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startLabel} – ${endLabel}`;
}

/** Rolling 28-day overview, advanced one aligned week at a time. */
export function CalendarMonthView() {
  const trip = useTripStore((s) => s.trip);
  const schedule = useTripStore((s) => s.schedule);
  const isReadOnly = useTripStore((s) => s.isReadOnly);
  const pois = useLibraryStore((s) => s.pois);
  const plannerFocusDate = useUIStore((s) => s.plannerFocusDate);
  const setPlannerFocusDate = useUIStore((s) => s.setPlannerFocusDate);
  const plannerCalendarStart = useUIStore((s) => s.plannerCalendarStart);
  const setPlannerCalendarStart = useUIStore((s) => s.setPlannerCalendarStart);
  const setPlannerMode = useUIStore((s) => s.setPlannerMode);
  const openAccommodationPicker = useUIStore((s) => s.openAccommodationPicker);
  const openNoteEditor = useUIStore((s) => s.openNoteEditor);
  const openLegEditor = useUIStore((s) => s.openLegEditor);

  const dayViews = useMemo(
    () => (trip ? computeDayViews(trip, schedule) : []),
    [trip, schedule],
  );
  const dayViewsByDate = useMemo(() => {
    return new Map(dayViews.map((view) => [view.date, view]));
  }, [dayViews]);
  const resolvedWindowStart = useMemo(() => {
    return resolvePlannerCalendarStart(
      dayViews,
      plannerCalendarStart,
      plannerFocusDate,
    );
  }, [dayViews, plannerCalendarStart, plannerFocusDate]);
  useEffect(() => {
    if (resolvedWindowStart && resolvedWindowStart !== plannerCalendarStart) {
      setPlannerCalendarStart(resolvedWindowStart);
    }
  }, [plannerCalendarStart, resolvedWindowStart, setPlannerCalendarStart]);
  const poiNameById = useMemo(
    () => new Map(pois.map((poi) => [poi.id, poi.name])),
    [pois],
  );
  const legs = useLegStore((state) => state.legs);
  const createLeg = useLegStore((state) => state.createLeg);
  const legsByDate = useMemo(() => {
    const lanes = packLegLanes(legs);
    const byDate = new Map<string, TripLeg[]>();
    for (const leg of legs) {
      for (const date of enumerateDates(leg.startDate, leg.endDate)) {
        const existing = byDate.get(date);
        if (existing) existing.push(leg);
        else byDate.set(date, [leg]);
      }
    }
    for (const dayLegs of byDate.values()) {
      dayLegs.sort((a, b) => (lanes.get(a.id) ?? 0) - (lanes.get(b.id) ?? 0));
    }
    return byDate;
  }, [legs]);

  const staySegments = useMemo(
    () =>
      trip
        ? deriveAccommodationStaySegments(
            enumerateDates(trip.startDate, trip.endDate),
            schedule.sleep,
          )
        : new Map<string, AccommodationStaySegment>(),
    [trip, schedule.sleep],
  );

  if (!trip || !resolvedWindowStart) return null;

  const limits = rollingWindowLimits(trip.startDate, trip.endDate);
  const windowStart = resolvedWindowStart;
  const dates = rollingWindowDates(windowStart);
  const windowEnd = dates[27]!;
  const canPrevious = windowStart > limits.firstStart;
  const canNext = windowStart < limits.lastStart;

  const moveWeek = (direction: -1 | 1) => {
    if ((direction < 0 && !canPrevious) || (direction > 0 && !canNext)) return;
    setPlannerCalendarStart(
      clampRollingWindowStart(
        addDaysToDate(windowStart, direction * 7),
        limits,
      ),
    );
  };

  const openDay = (date: string) => {
    setPlannerFocusDate(date);
    setPlannerMode("day");
  };
  const openNewNote = (date: string, zone: string) => {
    const startTime = zonedFromDateAndTime(date, "09:00", zone);
    openNoteEditor("create", undefined, {
      type: "timed",
      startTime,
      endTime: addMinutes(startTime, 60),
    });
  };
  const openNewAllDayNote = (startDate: string, endDate: string) => {
    openNoteEditor("create", undefined, {
      type: "all-day",
      startDate,
      endDate,
    });
  };
  // Legs are granular data, so this creates immediately rather than opening an
  // empty draft; the editor then opens on the real leg to name and colour it.
  const addNewLeg = (startDate: string, endDate: string) => {
    void (async () => {
      const leg = await createLeg(trip.id, {
        name: "New leg",
        startDate,
        endDate,
        color: legs.length % legPalettes.length,
      });
      if (leg) openLegEditor(leg.id);
    })();
  };
  const blockName = (block: DayBlock): string =>
    block.type === "poi"
      ? (poiNameById.get(block.scheduledPOI.poiId) ?? "Unknown place")
      : block.note.name;
  const readOnly = isReadOnly();
  const zoneForDate = (date: string) =>
    schedule.timezoneOverrides[date] ?? trip.defaultTimezone;

  return (
    <div
      className="custom-scrollbar min-h-0 flex-1 overflow-auto bg-page"
      data-testid="calendar-month-view"
    >
      <div className="mx-auto flex min-h-full w-full min-w-[56rem] max-w-6xl flex-col px-6 py-4">
        <div className="mb-3 flex shrink-0 items-center gap-2">
          <div className="flex gap-0.5">
            <Button
              variant="icon"
              icon="chevron_left"
              disabled={!canPrevious}
              onClick={() => moveWeek(-1)}
              title="Previous week"
              aria-label="Previous week"
            />
            <Button
              variant="icon"
              icon="chevron_right"
              disabled={!canNext}
              onClick={() => moveWeek(1)}
              title="Next week"
              aria-label="Next week"
            />
          </div>
          <h2 className="m-0 text-sm font-extrabold tracking-tight">
            {formatRange(windowStart, windowEnd)}
          </h2>
          <span className="rounded bg-subtle px-2 py-1 text-2xs font-extrabold uppercase tracking-wider text-secondary">
            Four weeks
          </span>
        </div>

        <div className="grid shrink-0 grid-cols-7 gap-1.5 pb-1.5">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="px-2 text-2xs font-extrabold uppercase tracking-wider text-tertiary"
            >
              {label}
            </div>
          ))}
        </div>

        <div className="grid flex-1 grid-cols-7 grid-rows-4 gap-1.5 pb-4">
          {dates.map((date, dateIndex) => {
            const inTrip = date >= trip.startDate && date <= trip.endDate;
            // The week row this cell sits in decides where a leg is clipped.
            const rowStartIndex = dateIndex - (dateIndex % 7);
            const rowStartDate = dates[rowStartIndex]!;
            const rowEndDate =
              dates[Math.min(rowStartIndex + 6, dates.length - 1)]!;
            const dayView = dayViewsByDate.get(date);
            const blocks = dayView?.blocks ?? [];
            const allDayBlocks = dayView?.allDayBlocks ?? [];
            const visibleAllDayBlocks = allDayBlocks.slice(0, MAX_CHIPS);
            const visibleTimedCount = Math.max(
              0,
              MAX_CHIPS - visibleAllDayBlocks.length,
            );
            const overflowAllDayBlocks = allDayBlocks.slice(MAX_CHIPS);
            const overflowBlocks = blocks.slice(visibleTimedCount);
            const overflow =
              overflowAllDayBlocks.length + overflowBlocks.length;
            const monthLabel = new Date(`${date}T00:00:00`).toLocaleDateString(
              "en-US",
              { month: "short" },
            );

            if (!inTrip) {
              return (
                <div
                  key={date}
                  className="min-h-24 rounded-lg border border-transparent bg-subtle/35 px-2 py-1.5 text-xs font-semibold text-tertiary/60"
                >
                  {Number(date.slice(8)) === 1 && `${monthLabel} `}
                  {Number(date.slice(8))}
                </div>
              );
            }

            const visibleBlocks = blocks.slice(0, visibleTimedCount);
            const zone =
              schedule.timezoneOverrides[date] ?? trip.defaultTimezone;
            const stay = staySegments.get(date);

            return (
              <MonthDateCell key={date} date={date} zone={zone}>
                <Button
                  variant="unstyled"
                  onClick={() => openDay(date)}
                  className="flex w-full cursor-pointer items-baseline gap-1.5 rounded text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-info"
                  aria-label={`Open day ${daysBetweenDates(trip.startDate, date) + 1} (${date}) in day view`}
                >
                  <span className="text-xs font-extrabold tabular-nums">
                    {Number(date.slice(8)) === 1 && `${monthLabel} `}
                    {Number(date.slice(8))}
                  </span>
                  <span className="text-2xs font-bold text-tertiary">
                    Day {daysBetweenDates(trip.startDate, date) + 1}
                  </span>
                  {blocks.length + allDayBlocks.length > 0 && (
                    <span className="ml-auto rounded bg-subtle px-1 py-px text-2xs font-extrabold tabular-nums text-secondary">
                      {blocks.length + allDayBlocks.length}
                    </span>
                  )}
                </Button>
                {legsByDate.get(date)?.map((leg) => {
                  const segment = legSegmentForDate(
                    leg,
                    date,
                    rowStartDate,
                    rowEndDate,
                  );
                  if (!segment) return null;
                  return (
                    <MonthLegChip
                      key={leg.id}
                      leg={leg}
                      segment={segment}
                      date={date}
                      showLabel={segment.isStart || dateIndex % 7 === 0}
                      isReadOnly={readOnly}
                      zoneForDate={zoneForDate}
                      onAutoNavigate={moveWeek}
                    />
                  );
                })}
                {visibleAllDayBlocks.map((block) => (
                  <MonthAllDayChip
                    key={block.note.id}
                    block={block}
                    date={date}
                    isReadOnly={readOnly}
                    zoneForDate={zoneForDate}
                    onAutoNavigate={moveWeek}
                  />
                ))}
                {visibleBlocks.map((block) => (
                  <MonthBlockChip
                    key={block.id}
                    block={block}
                    date={date}
                    displayZone={zone}
                    label={blockName(block)}
                    isReadOnly={readOnly}
                    onAutoNavigate={moveWeek}
                  />
                ))}
                {overflow > 0 && (
                  <details className="group/overflow">
                    <summary className="cursor-pointer list-none px-1.5 text-2xs font-semibold text-tertiary hover:text-primary">
                      +{overflow} more
                    </summary>
                    <div className="mt-1 flex flex-col gap-1">
                      {overflowAllDayBlocks.map((block) => (
                        <MonthAllDayChip
                          key={block.note.id}
                          block={block}
                          date={date}
                          isReadOnly={readOnly}
                          zoneForDate={zoneForDate}
                          onAutoNavigate={moveWeek}
                        />
                      ))}
                      {overflowBlocks.map((block) => (
                        <MonthBlockChip
                          key={block.id}
                          block={block}
                          date={date}
                          displayZone={zone}
                          label={blockName(block)}
                          isReadOnly={readOnly}
                          onAutoNavigate={moveWeek}
                        />
                      ))}
                    </div>
                  </details>
                )}
                <div className="mt-auto flex min-w-0 flex-col gap-1">
                  {!readOnly && (
                    <div className="flex min-w-0 items-stretch gap-1">
                      <MonthQuickAddAction
                        date={date}
                        label="Note"
                        ariaLabel={`Add note for ${date}`}
                        title="Click to add a timed note; drag across days to add an all-day note"
                        previewLabel="New all-day note"
                        onSelect={(startDate, endDate, dragged) =>
                          dragged
                            ? openNewAllDayNote(startDate, endDate)
                            : openNewNote(date, zone)
                        }
                      />
                      <MonthQuickAddAction
                        date={date}
                        label="Leg"
                        ariaLabel={`Add leg for ${date}`}
                        title="Click to add a one-day leg; drag across days to set its dates"
                        previewLabel="New leg"
                        onSelect={addNewLeg}
                      />
                    </div>
                  )}
                  <MonthAccommodation
                    date={date}
                    segment={stay}
                    name={stay ? (poiNameById.get(stay.poiId) ?? null) : null}
                    colorIndex={
                      stay
                        ? (schedule.accommodationConfigs[stay.poiId]?.color ??
                          0)
                        : 0
                    }
                    columnIndex={dateIndex % 7}
                    isReadOnly={readOnly}
                    onOpen={() => openAccommodationPicker(date)}
                    zoneForDate={zoneForDate}
                    onAutoNavigate={moveWeek}
                  />
                </div>
              </MonthDateCell>
            );
          })}
        </div>
      </div>
    </div>
  );
}
